import React, { useEffect, useRef, lazy, Component } from 'react';
import { ArrowUpRightIcon, LockIcon, PaletteIcon, PencilRulerIcon, ComponentIcon, FlaskConicalIcon, SearchIcon, RocketIcon } from 'lucide-react';
function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => el.classList.add('in-view'), delay);
        observer.disconnect();
      }
    }, {
      threshold: 0.08
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
}
const caseStudies = [{
  number: '01',
  name: 'Narra',
  logo: 'https://cdn.magicpatterns.com/uploads/ocLvdZc71ZNKULum6ZLUwF/Group_4.svg',
  tags: ['Brand Identity', 'Product Design', 'Design System'],
  description: 'Forma came to us as a fintech startup with a product but no identity. We built their brand from the ground up — logo, typography, color system — and translated it into a full component library used across their web app and investor materials.',
  impact: 'Building scalable Mental Wellness Infrastructure for the ~1.9M Workforce Behind a $2T Industry',
  placeholder: {
    bg: '#EFEFEF',
    accent: '#555555'
  },
  link: '#'
}, {
  number: '02',
  name: 'Pulse',
  logo: null,
  tags: ['Product Design', 'UX Research', '0→1'],
  description: 'A health-tech team needed to validate their monitoring dashboard before their Series A. We ran discovery sprints, mapped user flows with clinical staff, and designed a 0→1 product that shipped to beta within 10 weeks — with a research report that anchored their fundraise.',
  impact: null,
  placeholder: {
    bg: '#E5EDE8',
    accent: '#2A6A4A'
  },
  link: '#'
}];
const tagIcons: Record<string, React.ReactNode> = {
  'Brand Identity': <PaletteIcon size={11} data-id="element-209" />,
  'Product Design': <PencilRulerIcon size={11} data-id="element-210" />,
  'Design System': <ComponentIcon size={11} data-id="element-211" />,
  'UX Research': <SearchIcon size={11} data-id="element-212" />,
  '0→1': <RocketIcon size={11} data-id="element-213" />
};
export function Work() {
  const headerRef = useReveal();
  const card1Ref = useReveal(0);
  const card2Ref = useReveal(100);
  const testimonialRef = useReveal(100);
  const cardRefs = [card1Ref, card2Ref];
  return <section id="work" className="w-full pt-10 md:pt-16 pb-0" data-id="element-214">
      <div className="content-wrap px-6 md:px-12" data-id="element-215">
        {/* Section header */}
        <div ref={headerRef} className="reveal flex flex-col items-center text-center mb-8 md:mb-12 pt-8" style={{
        borderColor: 'var(--border)'
      }} data-id="element-216">
          <div data-id="element-217">
            <p className="text-xs tracking-[0.2em] uppercase mb-3 font-[300]" style={{
            color: 'var(--muted)'
          }} data-id="element-218">
              Selected Work
            </p>
            <h2 className="font-[250] text-4xl md:text-6xl tracking-[-0.03em]" style={{
            color: 'var(--fg)'
          }} data-id="element-219">
              Our work
            </h2>
          </div>
        </div>

        {/* Case study cards */}
        <div className="flex flex-col" data-id="element-220">
          {caseStudies.filter((_, i) => i !== 1).map((cs, i) => <div key={cs.name} data-id="element-221">
                <div ref={cardRefs[i]} className="reveal py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-20 items-start" data-id="element-222">
                  {/* Left: text content */}
                  <div className="flex flex-col items-center text-center md:items-start md:text-left" data-id="element-223">
                    {/* Name or logo */}
                    {cs.logo ? <img src={cs.logo} alt={cs.name} style={{
                height: 40,
                width: 'auto',
                objectFit: 'contain',
                marginBottom: '1.5rem'
              }} data-id="element-224" /> : <h3 className="font-semibold text-3xl md:text-4xl tracking-[-0.03em] mb-6 leading-tight" style={{
                color: 'var(--fg)'
              }} data-id="element-225">
                        {cs.name}
                      </h3>}

                    {/* Impact sub-header — larger, more dramatic */}
                    {cs.impact && <p className="font-medium text-xl md:text-2xl leading-snug mb-8" style={{
                color: 'var(--fg)',
                maxWidth: '36ch',
                letterSpacing: '-0.02em'
              }} data-id="element-226">
                        {cs.impact}
                      </p>}

                    {/* Mobile-only video — shown between header and description */}
                    <div className="block md:hidden rounded-2xl overflow-hidden mb-8 w-full" style={{
                aspectRatio: '1 / 1'
              }} data-id="element-227">
                      <video src="https://i.imgur.com/EQoAs5U.mp4" autoPlay loop muted playsInline preload="none" style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }} data-id="element-228" />
                    </div>

                    <p className="text-base leading-relaxed mb-10 text-[#777777]" style={{
                maxWidth: '52ch'
              }} data-id="element-229">
                      {'Narra partnered with us to build the product platform behind their vision for seafarer mental wellness. We launched a proof-of-concept and client pilot in under three months, then built the full platform ecosystem over the following seven months for a January 2026 launch. Since then, the platform has onboarded 5,000+ seafarers, providing services for them and their families.'}
                    </p>

                    {/* Link */}
                    <div className="flex items-center gap-3 mt-auto" data-id="element-230">
                      <span className="inline-flex items-center gap-1.5 text-xs font-[300] px-3 py-1.5 rounded-full" style={{
                  background: 'rgba(0,0,0,0.05)',
                  color: 'var(--muted)'
                }} data-id="element-231">
                        <span className="w-1.5 h-1.5 rounded-full" style={{
                    background: 'var(--muted)',
                    opacity: 0.5
                  }} data-id="element-232" />
                        Coming soon
                      </span>
                      <a href={cs.link} className="inline-flex items-center gap-1.5 text-xs font-medium transition-colors duration-200 text-[#4e4e4e]" style={{
                  pointerEvents: 'none',
                  opacity: 0.4
                }} tabIndex={-1} aria-disabled="true" data-id="element-233">
                        View case study
                        <ArrowUpRightIcon size={11} data-id="element-234" />
                      </a>
                    </div>
                  </div>

                  {/* Right: desktop-only video */}
                  <div className="hidden md:flex flex-col gap-4" data-id="element-235">
                    <div className="rounded-2xl w-full overflow-hidden" style={{
                aspectRatio: '1 / 1'
              }} data-id="element-236">
                      <video src="https://i.imgur.com/EQoAs5U.mp4" autoPlay loop muted playsInline preload="none" style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }} data-id="element-237" />
                    </div>
                  </div>
                </div>

                {/* Featured testimonial — Narra */}
                {cs.name === 'Narra' && <div ref={testimonialRef} className="reveal mb-8 md:mb-16 pb-8 md:pb-16 grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20 items-center" data-id="element-238">
                    {/* Left: image (flipped to come first) */}
                    <div className="hidden md:flex flex-col justify-center" data-id="element-239">
                      <div className="rounded-2xl overflow-hidden" style={{
                width: '100%'
              }} data-id="element-240">
                        <img src="https://i.imgur.com/vMm7wb5.gif" alt="Narra app preview" loading="lazy" style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: 16
                }} data-id="element-241" />
                      </div>
                    </div>

                    {/* Right: quote — centered */}
                    <div className="flex flex-col items-center text-center" data-id="element-242">
                      <p className="font-normal text-base leading-relaxed mb-6 text-[#4e4e4e]" style={{
                letterSpacing: '-0.01em',
                maxWidth: '42ch'
              }} data-id="element-243">
                        The GoLaunch team has been our right hand in building
                        Narra Health. From the very beginning, we never saw them
                        as just a service provider, but as true team members who
                        stepped into our shoes, understood our mission, and
                        rallied alongside us for the cause we deeply care about.
                        Narra Health would not exist today without GoLaunch.
                      </p>
                      <p className="font-normal text-base leading-relaxed mb-8 text-[#4e4e4e]" style={{
                letterSpacing: '-0.01em',
                maxWidth: '42ch'
              }} data-id="element-244">
                        Their product managers, designers, engineers,
                        developers, and the entire team have played a vital role
                        in turning our ideas into something real and meaningful.
                        There is no other product team we trust more. We are
                        incredibly grateful for this partnership and excited to
                        see what else we will build together.
                      </p>
                      <div className="flex items-center gap-3" data-id="element-245">
                        <img src="https://cdn.magicpatterns.com/uploads/i4TJBBrfNgK9GYJdVSXr32/karina_image.jpg" alt="Karina Garcia" className="w-10 h-10 rounded-full flex-shrink-0" style={{
                  objectFit: 'cover'
                }} data-id="element-246" />
                        <div className="text-left" data-id="element-247">
                          <p className="text-sm font-medium tracking-tight" style={{
                    color: 'var(--fg)'
                  }} data-id="element-248">
                            Karina Garcia
                          </p>
                          <p className="text-xs font-[300]" style={{
                    color: 'var(--muted)'
                  }} data-id="element-249">
                            Co-founder & CEO · Narra
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Divider between cards */}
                {i < caseStudies.length - 1 && <hr style={{
            border: 'none',
            borderTop: '1px solid var(--border)',
            opacity: 0.6
          }} data-id="element-250" />}
              </div>)}
        </div>
      </div>
    </section>;
}